import { defineComponent, inject, PropType, ref, watch, computed, onMounted } from 'vue';
import SButton from '@/components/stemdo-components/s-button/SButton.vue';
import SModalCentered from '@/components/stemdo-components/s-modal-centered/SModalCentered.vue';
import TeamRequestService from '@/sections/admin/entities/team-request/team-request.service';
import { TeamRequest } from '@/shared/model/team-request.model';
import SModalTeamRequestConfirmation from '@/components/stemdo-components/s-modal-team-request-confirmation/SModalTeamRequestConfirmation.vue';
import { RequestStatus } from '@/shared/model/enumerations/request-status.model';
import { useStore } from '@/plugins/vuex';
import axios from 'axios';
import SModalStemdoerNotAvailable from '@/components/stemdo-components/s-modal-stemdoer-not-available/SModalStemdoerNotAvailable.vue';
import SModalNewClientCreator from '@/components/stemdo-components/s-modal-new-client-creator/SModalNewClientCreator.vue';
import { Stemdoers } from '@/shared/model/stemdoer.model';
import SSelectorMultiple from '@/components/stemdo-components/s-selector-multiple/SSelectorMultiple.vue';
import { IClient } from '@/shared/model/client.model';
import ClientService from '@/sections/admin/entities/client/client.service';
import { useI18N } from '@/plugins/i18n';
import SPopUp from '@/components/stemdo-components/s-pop-up/SPopUp.vue';
import RegisterService from '@/sections/account/register/register.service';
import UserManagementService from '@/sections/admin/user-management/user-management.service';
import localStorageFacade, { StorageKey } from '@/shared/local-store-facade/LocalStoreFacade';
import { StemdoerRates } from '@/shared/model/stemdoer-rate.model';

export default defineComponent({
  props: {
    teamRequest: {
      type: Object as () => TeamRequest,
      required: true
    },
    onOpenModal: {
      type: Function,
      required: true
    }
  },
  components: {
    SButton,
    SModalCentered,
    SSelectorMultiple,
    SPopUp,
    SModalNewClientCreator,
    SModalTeamRequestConfirmation,
    SModalStemdoerNotAvailable
  },
  setup(props) {
    const store = useStore();
    const createApprovedTeamRequestCheckBox = ref(store.state.checkoutStore.createApprovedTeamRequestCheckBox ?? false);
    const notifyClientByEmailCheckbox = ref(store.state.checkoutStore.notifyClientByEmailCheckbox ?? false);
    const teamRequestService: TeamRequestService = inject('teamRequestService');
    const clientService = inject('clientService') as () => ClientService;
    const isTeamRequestConfirmationModalOpen = ref(false);
    const isStemdoersNotAvailableModalOpen = ref(false);
    const stemdoersNotAvailables = ref<Stemdoers>(Stemdoers.empty());
    const companySelected = computed(() => store.getters['cartStore/companySelected']);
    const clientSelected = computed(() => store.getters['cartStore/clientSelected']);
    const companyClients = ref<IClient[]>([]);
    const clientItems = ref([]);
    const selectedClients = ref<string[]>([]);
    const mutableTeamRequest = ref({});
    const errorPopUp = ref(false);
    const isNewClientModalOpen = ref(false);
    const loadingTeamRequest = ref(false);

    const i18n = useI18N();
    const registerService: RegisterService = inject('registerService');
    const userManagementService: UserManagementService = new UserManagementService();

    const openTeamRequestConfirmationModal = () => {
      isTeamRequestConfirmationModalOpen.value = true;
    };

    const closeTeamRequestConfirmationModal = () => {
      isTeamRequestConfirmationModalOpen.value = false;
    };

    const openStemdoersNotAvailableModal = () => {
      isStemdoersNotAvailableModalOpen.value = true;
    };
    const closeStemdoersNotAvailableModal = () => {
      isStemdoersNotAvailableModalOpen.value = false;
    };

    const openNewClientModal = () => {
      isNewClientModalOpen.value = true;
    };
    const closeNewClientModal = () => {
      isNewClientModalOpen.value = false;
    };

    const addNewClient = async (name: string, surname: string, email: string, sendWelcomeEmail: boolean) => {
      const client = {
        name,
        surname,
        email,
        phone: '600001234',
        departmentId: 7,
        company: {
          cif: companySelected.value.cif
        }
      };
      try {
        const userResponse = await registerService.processRegistrationInCheckout({}, client, sendWelcomeEmail);
        if (userResponse.data.id) {
          await userManagementService.active(userResponse.data.id);
        }
        await fetchCompanyClients(email);
        closeNewClientModal();
      } catch (err) {
        console.error('Error adding new client:', err);
        if (err && err.response) {
          console.error('Error response:', err.response);
        }
        handleError();
      }
    };

    const handleError = () => {
      errorPopUp.value = true;
    };

    const handleClosePopUp = () => {
      errorPopUp.value = false;
    };

    const fetchCompanyClients = async (newClientEmail?: string) => {
      try {
        companyClients.value = await clientService().retrieveByCompanyId(companySelected.value.id);
        updateClientItems();
        if (newClientEmail) {
          const newClient = companyClients.value.find(client => client.email === newClientEmail);
          if (newClient && !selectedClients.value.includes(newClient.id)) {
            selectedClients.value = [...selectedClients.value, newClient.id];
          }
        }
      } catch (err) {
        console.error('Error retrieving company clients:', err);
      }
    };

    const updateTeamRequestClients = () => {
      const selectedFullClients = companyClients.value.filter(client => selectedClients.value.includes(client.id));
      props.teamRequest.associatedClients = selectedFullClients.map(client => ({
        clientId: client.id,
        name: client.name,
        surname: client.surname,
        email: client.email
      }));
    };

    const updateClientItems = () => {
      const clientsSet = new Set([
        {
          id: 'VIEW_ALL',
          desc: i18n.t('checkout.buttonGroup.manager.checkout.all') as string
        }
      ]);
      companyClients.value.forEach(client => {
        clientsSet.add({
          id: client.id,
          desc: `${client.name} ${client.surname} (${client.email})`
        });
      });
      clientItems.value = Array.from(clientsSet);
      updateTeamRequestClients();
    };

    // Normaliza los stemdoerRates para asegurar estructura consistente
    function normalizeStemdoerRates(rates: any[]): any[] {
      const cartStemdoers = store.getters['cartStore/cartItems'] || [];
      return (Array.isArray(rates) ? rates : Object.values(rates)).map(r => {
        let stemdoer = r.stemdoer || (r.stemdoerId ? { id: r.stemdoerId } : null);
        if (stemdoer && stemdoer.id) {
          const full = cartStemdoers.find((s: any) => s.id === stemdoer.id);
          if (full) stemdoer = { ...full, ...stemdoer };
        }
        return {
          ...r,
          stemdoer
        };
      });
    }

    const createTeamRequest = (teamRequest: TeamRequest) => {
      loadingTeamRequest.value = true;
      const selectedFullClients = companyClients.value.filter(client => selectedClients.value.includes(client.id));
      teamRequest.client = selectedFullClients[0] ? { id: selectedFullClients[0].id } : undefined;
      if (selectedFullClients.length === 0) {
        handleError();
        loadingTeamRequest.value = false;
        throw new Error('Select at least one client');
      }
      teamRequest.associatedClients = [];
      selectedFullClients.forEach(client => {
        teamRequest.associatedClients.push({
          clientId: client.id,
          name: client.name,
          surname: client.surname,
          email: client.email
        });
      });

      if (createApprovedTeamRequestCheckBox.value) {
        teamRequest.status = RequestStatus.ON_GOING;
      } else {
        teamRequest.status = RequestStatus.VALIDATED;
      }

      teamRequest.skipOnGoingEmail = notifyClientByEmailCheckbox.value;

      if (teamRequest.stemdoerRates) {
        teamRequest.stemdoerRates = new StemdoerRates(normalizeStemdoerRates(teamRequest.stemdoerRates));
      }

      teamRequestService
        .create(teamRequest)
        .then(res => {
          store.dispatch('checkoutStore/clearCheckout');
          props.onOpenModal(teamRequest);
        })
        .catch(error => {
          if (error.response && error.response.status === 400) {
            stemdoersNotAvailables.value = error.response.data.stemdoerRates.map(item => item.stemdoer);
            openStemdoersNotAvailableModal();
          } else {
            console.error(error);
            handleError();
          }
        })
        .finally(() => {
          loadingTeamRequest.value = false;
          onClickTeamRequestConfirmationModal;
        });
    };

    const onClickTeamRequestConfirmationModal = (teamRequestName: string, initDate: string, endDate: string, jobDescription: string) => {
      const mutableTeamRequest = { ...props.teamRequest };
      mutableTeamRequest.name = teamRequestName;
      mutableTeamRequest.initDate = new Date(initDate);
      mutableTeamRequest.endDate = endDate ? new Date(endDate) : null;
      mutableTeamRequest.jobDescription = jobDescription;
      createTeamRequest(mutableTeamRequest);
    };

    const removeSelectedClient = (clientId: string) => {
      selectedClients.value = selectedClients.value.filter(id => id !== clientId);
    };

    const restorePersistedClients = () => {
      let persistedClients = [];
      let persistedCompanyId = null;
      try {
        const raw = localStorageFacade.get(StorageKey.CHECKOUT_STORE);
        if (raw) {
          const obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
          persistedClients = obj.selectedClients || [];
          persistedCompanyId = obj.companyId;
        }
      } catch {}
      const currentCompanyId = companySelected.value?.id;
      if (persistedClients.length > 0 && persistedCompanyId === currentCompanyId) {
        const validIds = persistedClients.filter(id => clientItems.value.some(item => item.id === id));
        selectedClients.value = [...validIds];
      }
    };

    const restorePersistedTeamRequestMutable = () => {
      let persistedTeamRequest = {};
      let persistedCompanyId = null;
      try {
        const raw = localStorageFacade.get(StorageKey.CHECKOUT_STORE);
        if (raw) {
          const obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
          if (obj && obj.mutableTeamRequest) {
            persistedTeamRequest = obj.mutableTeamRequest;
            persistedCompanyId = obj.companyId;
          }
        }
      } catch (e) {
        console.error('Error parsing storage:', e);
      }
      const currentCompanyId = companySelected.value?.id;
      if (persistedTeamRequest && persistedCompanyId === currentCompanyId) {
        mutableTeamRequest.value = persistedTeamRequest;
        return persistedTeamRequest;
      }
      mutableTeamRequest.value = {};
      return null;
    };

    const ensureCheckoutCompanySync = () => {
      try {
        const checkoutRaw = localStorageFacade.get(StorageKey.CHECKOUT_STORE);
        const companyRaw = localStorage.getItem('companySelected');
        if (!checkoutRaw || !companyRaw) return;
        let checkoutObj, companyObj;
        try {
          checkoutObj = typeof checkoutRaw === 'string' ? JSON.parse(checkoutRaw) : checkoutRaw;
        } catch (e) {
          checkoutObj = null;
        }
        try {
          companyObj = typeof companyRaw === 'string' ? JSON.parse(companyRaw) : companyRaw;
        } catch (e) {
          companyObj = null;
        }
        if (!checkoutObj || !companyObj) {
          return;
        }
        if (checkoutObj.companyId && companyObj.id && checkoutObj.companyId !== companyObj.id) {
          store.dispatch('checkoutStore/clearCheckout');
        }
      } catch (e) {
        console.error('CompanySync: Error:', e);
      }
    };

    watch(createApprovedTeamRequestCheckBox, val => {
      store.dispatch('checkoutStore/updateCreateApprovedTeamRequestCheckBox', val);
    });

    watch(createApprovedTeamRequestCheckBox, newVal => {
      if (!newVal) {
        notifyClientByEmailCheckbox.value = false;
      }
    });

    onMounted(async () => {
      ensureCheckoutCompanySync();
      await fetchCompanyClients();
      store.commit('cartStore/setClient', null);
      restorePersistedClients();
      const persistedTeamRequest = restorePersistedTeamRequestMutable();
      try {
        const raw = localStorageFacade.get(StorageKey.CHECKOUT_STORE);
        if (raw) {
          const obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
          if (obj && typeof obj.createApprovedTeamRequestCheckBox === 'boolean') {
            createApprovedTeamRequestCheckBox.value = obj.createApprovedTeamRequestCheckBox;
          }
        }
      } catch (e) {
        console.error(e);
      }
      if (persistedTeamRequest) {
        Object.assign(props.teamRequest, persistedTeamRequest);
      }
      try {
        const rates = store.state.checkoutStore.stemdoerRates;
        const normalizedRates = normalizeStemdoerRates(rates);
        props.teamRequest.stemdoerRates = new StemdoerRates(normalizedRates);
      } catch (e) {
        console.error('Error synchronizing rates:', e);
      }
    });

    watch(
      () => companySelected.value?.id,
      (newId, oldId) => {
        if (oldId && newId && newId !== oldId) {
          ensureCheckoutCompanySync();
        }
      }
    );

    watch(() => i18n.locale, updateClientItems);

    const syncClientSelectedWithStore = () => {
      if (selectedClients.value.length > 0) {
        const firstClientId = selectedClients.value[0];
        const firstClient = companyClients.value.find(c => c.id === firstClientId) || null;
        store.commit('cartStore/setClient', firstClient);
      } else {
        store.commit('cartStore/setClient', null);
      }
    };

    watch(selectedClients, () => {
      syncClientSelectedWithStore();
      updateTeamRequestClients();
      // Sincroniza responsables con checkoutStore para persistencia, asegurando que se guarda el companyId
      store.dispatch('checkoutStore/updateSelectedClients', selectedClients.value);
    });

    return {
      createApprovedTeamRequestCheckBox,
      notifyClientByEmailCheckbox,
      isTeamRequestConfirmationModalOpen,
      isStemdoersNotAvailableModalOpen,
      stemdoersNotAvailables,
      companyClients,
      clientItems,
      selectedClients,
      errorPopUp,
      isNewClientModalOpen,
      loadingTeamRequest,
      openTeamRequestConfirmationModal,
      closeTeamRequestConfirmationModal,
      openStemdoersNotAvailableModal,
      closeStemdoersNotAvailableModal,
      openNewClientModal,
      closeNewClientModal,
      onClickTeamRequestConfirmationModal,
      removeSelectedClient,
      handleError,
      handleClosePopUp,
      addNewClient
    };
  }
});
